import React from 'react';
import { Helmet } from 'react-helmet-async';
import { SITE, absoluteUrl } from './site.mjs';

/**
 * Meta tags por página. Se renderiza en el <head> vía react-helmet-async y el
 * script de prerender los captura en el HTML estático de cada ruta.
 *
 * @param {object} props
 * @param {string} props.title        Título (sin el nombre de la empresa; se añade automáticamente)
 * @param {string} props.description  Meta description (ideal 120–160 caracteres)
 * @param {string} props.path         Ruta canónica, p. ej. "/servicios/desarrollo-web"
 * @param {string} [props.image]      URL absoluta de imagen OG (1200x630)
 * @param {string} [props.type]       og:type (website | article | product)
 * @param {boolean} [props.noindex]   true para páginas que no deben indexarse
 * @param {object|object[]} [props.jsonLd]  Uno o varios objetos schema.org
 * @param {boolean} [props.titleTemplate=true] false para usar el título literal
 * @param {string} [props.lang]       Idioma del contenido de la página (por defecto el del sitio)
 * @param {Array<{hrefLang:string, href:string}>} [props.alternates] Enlaces hreflang a variantes de la página
 */
const Seo = ({
    title,
    description = SITE.defaultDescription,
    path = '/',
    image = SITE.ogImage,
    type = 'website',
    noindex = false,
    jsonLd,
    titleTemplate = true,
    lang = SITE.language,
    alternates = [],
}) => {
    const fullTitle = titleTemplate && title ? `${title} | ${SITE.name}` : title || SITE.name;
    const url = absoluteUrl(path);
    const schemas = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

    return (
        <Helmet prioritizeSeoTags>
            <html lang={lang} />
            <title>{fullTitle}</title>
            <meta name="description" content={description} />
            <link rel="canonical" href={url} />
            {alternates.map((a) => (
                <link key={a.hrefLang} rel="alternate" hrefLang={a.hrefLang} href={absoluteUrl(a.href)} />
            ))}
            <meta
                name="robots"
                content={noindex ? 'noindex, nofollow' : 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1'}
            />

            <meta property="og:type" content={type} />
            <meta property="og:site_name" content={SITE.name} />
            <meta property="og:locale" content={SITE.locale} />
            <meta property="og:title" content={fullTitle} />
            <meta property="og:description" content={description} />
            <meta property="og:url" content={url} />
            <meta property="og:image" content={image} />
            <meta property="og:image:width" content="1200" />
            <meta property="og:image:height" content="630" />
            <meta property="og:image:alt" content={fullTitle} />

            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={fullTitle} />
            <meta name="twitter:description" content={description} />
            <meta name="twitter:image" content={image} />

            {schemas.map((schema, i) => (
                <script key={i} type="application/ld+json">
                    {JSON.stringify(schema)}
                </script>
            ))}
        </Helmet>
    );
};

export default Seo;
